import { useEffect, useState, useCallback } from "react";
import { useApp } from "@/context/AppContext";
import api, { apiError } from "@/lib/api";
import { PageHeader, EmptyState } from "@/components/PageHeader";
import { KpiCard } from "@/components/KpiCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { fmtDA, fmtInt, fmtDate } from "@/lib/format";
import { Plus, Package, Loader2, Boxes, AlertTriangle, ArrowUpDown, Pencil, ImageIcon } from "lucide-react";
import { toast } from "sonner";
import { fileUrl } from "@/lib/files";

const CAT_LABEL = { matiere_premiere: "Matière première", produit_fini: "Produit fini" };

export default function Stock() {
  const { societes, societeParam, societeId } = useApp();
  const [produits, setProduits] = useState([]);
  const [mouvements, setMouvements] = useState([]);
  const [valeur, setValeur] = useState({ valeur: 0, nb_produits: 0, alertes_stock_min: [] });
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(null);
  const [adjFor, setAdjFor] = useState(null);
  const [adj, setAdj] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [p, m, v] = await Promise.all([
        api.get("/produits", { params: societeParam }),
        api.get("/stock/mouvements", { params: societeParam }),
        api.get("/stock/valeur", { params: societeParam }),
      ]);
      setProduits(p.data); setMouvements(m.data.items); setValeur(v.data);
    } catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  }, [societeId]); // eslint-disable-line
  useEffect(() => { load(); }, [load]);

  const sid = () => (societeId !== "all" ? societeId : societes[0]?.id || "");
  const emptyForm = () => ({ id: null, societe_id: sid(), nom: "", reference: "", categorie: "", conditionnement: "", unite: "unité", prix_achat: 0, prix_vente: 0, stock_min: 0, quantite: 0, photo_id: "" });
  const openNew = () => { setForm(emptyForm()); setOpen(true); };
  const openEdit = (p) => { setForm({ id: p.id, societe_id: p.societe_id, nom: p.nom, reference: p.reference || "", categorie: p.categorie || "", conditionnement: p.conditionnement || "", unite: p.unite || "unité", prix_achat: p.prix_achat || 0, prix_vente: p.prix_vente || 0, stock_min: p.stock_min || 0, quantite: p.quantite || 0, photo_id: p.photo_id || "" }); setOpen(true); };

  const [uploading, setUploading] = useState(false);
  const uploadPhoto = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, photo_id: res.data.id }));
      toast.success("Photo ajoutée.");
    } catch (e) { toast.error(apiError(e)); } finally { setUploading(false); }
  };

  const submit = async () => {
    if (!form.societe_id || !form.nom) return toast.error("Société et nom requis.");
    const payload = { ...form, prix_achat: Number(form.prix_achat) || 0, prix_vente: Number(form.prix_vente) || 0, stock_min: Number(form.stock_min) || 0, quantite: Number(form.quantite) || 0 };
    try {
      if (form.id) await api.put(`/produits/${form.id}`, payload);
      else await api.post("/produits", payload);
      toast.success(form.id ? "Produit modifié." : "Produit créé."); setOpen(false); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const openAdj = (p) => { setAdjFor(p); setAdj({ sens: "entree", quantite: "", motif: "ajustement" }); };
  const submitAdj = async () => {
    if (!adj.quantite || Number(adj.quantite) <= 0) return toast.error("Quantité invalide.");
    try {
      await api.post(`/produits/${adjFor.id}/ajustement`, { ...adj, quantite: Number(adj.quantite) });
      toast.success("Stock ajusté."); setAdjFor(null); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div>
      <PageHeader title="Stock" subtitle="Produits, quantités disponibles et mouvements" testid="stock-page"
        actions={<Button onClick={openNew} className="gap-2" data-testid="new-produit-btn"><Plus className="h-4 w-4" /> Nouveau produit</Button>} />

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6 kpi-grid">
        <KpiCard testid="stock-valeur" label="Valeur du stock" value={valeur.valeur} icon={Boxes} tone="primary" />
        <KpiCard testid="stock-nb" label="Produits" value={valeur.nb_produits} isCurrency={false} icon={Package} />
        <KpiCard testid="stock-alertes" label="Alertes stock mini" value={valeur.alertes_stock_min.length} isCurrency={false} icon={AlertTriangle} tone={valeur.alertes_stock_min.length ? "danger" : "success"} />
      </div>

      <Tabs defaultValue="produits">
        <TabsList data-testid="stock-tabs">
          <TabsTrigger value="produits" className="gap-1.5"><Package className="h-4 w-4" /> Produits</TabsTrigger>
          <TabsTrigger value="mouvements" className="gap-1.5"><ArrowUpDown className="h-4 w-4" /> Mouvements</TabsTrigger>
        </TabsList>

        <TabsContent value="produits" className="mt-4">
          <div className="rounded-xl border bg-card overflow-hidden">
            {loading ? <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
              : produits.length === 0 ? <EmptyState icon={Package} title="Aucun produit" hint="Créez des produits pour suivre les entrées, sorties et quantités." />
              : (
                <div className="overflow-x-auto">
                  <Table data-testid="produits-table">
                    <TableHeader><TableRow className="bg-muted/50"><TableHead>Produit</TableHead><TableHead>Référence</TableHead><TableHead>Catégorie</TableHead><TableHead className="text-right">Prix achat</TableHead><TableHead className="text-right">Stock</TableHead><TableHead className="text-right">Valeur</TableHead><TableHead></TableHead></TableRow></TableHeader>
                    <TableBody>
                      {produits.map((p) => {
                        const alert = p.stock_min > 0 && p.quantite <= p.stock_min;
                        return (
                          <TableRow key={p.id} data-testid={`produit-row-${p.id}`}>
                            <TableCell className="font-medium">{p.nom}{p.conditionnement && <span className="text-xs text-muted-foreground"> · {p.conditionnement}</span>}</TableCell>
                            <TableCell className="tabular text-sm">{p.reference || "—"}</TableCell>
                            <TableCell className="text-sm">{CAT_LABEL[p.categorie] ? <Badge variant="secondary" className="text-[10px]">{CAT_LABEL[p.categorie]}</Badge> : <span className="text-muted-foreground">—</span>}</TableCell>
                            <TableCell className="tabular text-right text-sm">{fmtDA(p.prix_achat)}</TableCell>
                            <TableCell className="text-right"><span className={`tabular font-semibold ${alert ? "text-destructive" : ""}`}>{fmtInt(p.quantite)} {p.unite}</span>{alert && <Badge variant="secondary" className="ml-2 bg-destructive/10 text-destructive text-[10px]">mini</Badge>}</TableCell>
                            <TableCell className="tabular text-right text-sm">{fmtDA((p.quantite || 0) * (p.prix_achat || 0))}</TableCell>
                            <TableCell className="text-right whitespace-nowrap"><Button size="sm" variant="ghost" className="h-8 gap-1.5" onClick={() => openEdit(p)} data-testid={`edit-btn-${p.id}`}><Pencil className="h-3.5 w-3.5" /> Modifier</Button><Button size="sm" variant="outline" className="h-8 gap-1.5 ml-1" onClick={() => openAdj(p)} data-testid={`adj-btn-${p.id}`}><ArrowUpDown className="h-3.5 w-3.5" /> Ajuster</Button></TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              )}
          </div>
        </TabsContent>

        <TabsContent value="mouvements" className="mt-4">
          <div className="rounded-xl border bg-card overflow-hidden">
            {mouvements.length === 0 ? <EmptyState icon={ArrowUpDown} title="Aucun mouvement" hint="Les réceptions et les ventes génèrent des mouvements automatiquement." />
              : (
                <div className="overflow-x-auto">
                  <Table data-testid="stock-mouvements-table">
                    <TableHeader><TableRow className="bg-muted/50"><TableHead>Date</TableHead><TableHead>Produit</TableHead><TableHead>Motif</TableHead><TableHead>Réf.</TableHead><TableHead className="text-right">Avant</TableHead><TableHead className="text-right">Mouvement</TableHead><TableHead className="text-right">Après</TableHead></TableRow></TableHeader>
                    <TableBody>
                      {mouvements.map((m) => (
                        <TableRow key={m.id}>
                          <TableCell className="tabular text-sm whitespace-nowrap">{fmtDate(m.date)}</TableCell>
                          <TableCell className="text-sm">{m.product_name}</TableCell>
                          <TableCell className="text-sm capitalize">{(m.motif || "").replace(/_/g, " ")}</TableCell>
                          <TableCell className="tabular text-xs">{m.ref_document || "—"}</TableCell>
                          <TableCell className="tabular text-right text-muted-foreground">{fmtInt(m.quantite_avant)}</TableCell>
                          <TableCell className={`tabular text-right font-semibold ${m.sens === "entree" ? "text-success" : "text-destructive"}`}>{m.sens === "entree" ? "+" : "−"}{fmtInt(m.quantite)}</TableCell>
                          <TableCell className="tabular text-right font-medium">{fmtInt(m.quantite_apres)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
          </div>
        </TabsContent>
      </Tabs>

      {/* Create product */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-popover max-w-lg" data-testid="produit-dialog">
          <DialogHeader><DialogTitle className="font-heading text-xl">{form?.id ? "Modifier le produit" : "Nouveau produit"}</DialogTitle><DialogDescription>Renseignez les informations du produit, son prix d'achat et sa photo.</DialogDescription></DialogHeader>
          {form && (
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5 col-span-2">
                <Label>Société</Label>
                <Select value={form.societe_id} onValueChange={(v) => setForm((f) => ({ ...f, societe_id: v }))}>
                  <SelectTrigger data-testid="produit-societe"><SelectValue placeholder="Choisir" /></SelectTrigger>
                  <SelectContent className="bg-popover">{societes.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5 col-span-2"><Label>Nom *</Label><Input data-testid="produit-nom" value={form.nom} onChange={(e) => setForm((f) => ({ ...f, nom: e.target.value }))} /></div>
              <div className="space-y-1.5"><Label>Référence</Label><Input data-testid="produit-reference" value={form.reference} onChange={(e) => setForm((f) => ({ ...f, reference: e.target.value }))} /></div>
              <div className="space-y-1.5">
                <Label>Catégorie</Label>
                <Select value={form.categorie || "none"} onValueChange={(v) => setForm((f) => ({ ...f, categorie: v === "none" ? "" : v }))}>
                  <SelectTrigger data-testid="produit-categorie"><SelectValue placeholder="Non classé" /></SelectTrigger>
                  <SelectContent className="bg-popover">
                    <SelectItem value="none">Non classé</SelectItem>
                    <SelectItem value="matiere_premiere">Matière première</SelectItem>
                    <SelectItem value="produit_fini">Produit fini</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5"><Label>Conditionnement</Label><Input value={form.conditionnement} onChange={(e) => setForm((f) => ({ ...f, conditionnement: e.target.value }))} placeholder="Ex: 10 kg" /></div>
              <div className="space-y-1.5"><Label>Unité</Label><Input value={form.unite} onChange={(e) => setForm((f) => ({ ...f, unite: e.target.value }))} placeholder="unité, kg, carton…" /></div>
              <div className="space-y-1.5"><Label>Prix d'achat</Label><Input type="number" value={form.prix_achat} onChange={(e) => setForm((f) => ({ ...f, prix_achat: e.target.value }))} /></div>
              <div className="space-y-1.5"><Label>Prix de vente</Label><Input type="number" value={form.prix_vente} onChange={(e) => setForm((f) => ({ ...f, prix_vente: e.target.value }))} /></div>
              <div className="space-y-1.5"><Label>Stock minimum</Label><Input type="number" value={form.stock_min} onChange={(e) => setForm((f) => ({ ...f, stock_min: e.target.value }))} /></div>
              <div className="space-y-1.5"><Label>Stock initial</Label><Input type="number" value={form.quantite} onChange={(e) => setForm((f) => ({ ...f, quantite: e.target.value }))} disabled={!!form.id} /></div>
              <div className="space-y-1.5 col-span-2">
                <Label>Photo du produit <span className="text-muted-foreground text-xs font-normal">(affichée sur la fiche technique)</span></Label>
                <div className="flex items-center gap-3">
                  <div className="h-16 w-16 rounded-lg border bg-muted/40 flex items-center justify-center overflow-hidden shrink-0">
                    {form.photo_id ? <img src={fileUrl(form.photo_id)} alt="aperçu" className="h-full w-full object-cover" data-testid="produit-photo-preview" /> : <ImageIcon className="h-6 w-6 text-muted-foreground" />}
                  </div>
                  <div className="flex items-center gap-2">
                    <Input type="file" accept="image/png,image/jpeg,image/webp" className="text-xs" onChange={(e) => uploadPhoto(e.target.files?.[0])} data-testid="produit-photo-input" />
                    {uploading && <Loader2 className="h-4 w-4 animate-spin text-primary" />}
                    {form.photo_id && !uploading && <Button type="button" variant="ghost" size="sm" className="text-destructive" onClick={() => setForm((f) => ({ ...f, photo_id: "" }))} data-testid="produit-photo-remove">Retirer</Button>}
                  </div>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annuler</Button>
            <Button onClick={submit} data-testid="produit-submit">Enregistrer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Adjustment */}
      <Dialog open={!!adjFor} onOpenChange={(v) => !v && setAdjFor(null)}>
        <DialogContent className="bg-popover max-w-sm" data-testid="adj-dialog">
          <DialogHeader><DialogTitle className="font-heading text-xl">Ajuster le stock</DialogTitle></DialogHeader>
          {adjFor && adj && (
            <div className="space-y-4">
              <p className="text-sm">{adjFor.nom} — stock actuel : <span className="tabular font-semibold">{fmtInt(adjFor.quantite)}</span></p>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5"><Label>Sens</Label>
                  <Select value={adj.sens} onValueChange={(v) => setAdj((a) => ({ ...a, sens: v }))}>
                    <SelectTrigger data-testid="adj-sens"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-popover"><SelectItem value="entree">Entrée (+)</SelectItem><SelectItem value="sortie">Sortie (−)</SelectItem></SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5"><Label>Quantité *</Label><Input data-testid="adj-quantite" type="number" value={adj.quantite} onChange={(e) => setAdj((a) => ({ ...a, quantite: e.target.value }))} /></div>
                <div className="space-y-1.5 col-span-2"><Label>Motif</Label><Input value={adj.motif} onChange={(e) => setAdj((a) => ({ ...a, motif: e.target.value }))} placeholder="casse, perte, inventaire…" /></div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setAdjFor(null)}>Annuler</Button>
            <Button onClick={submitAdj} data-testid="adj-submit">Valider</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
